<template>
  <v-card>
    <v-data-table
      :expanded.sync="expanded"
      fixed-header
      :headers="headers"
      :height="height - tableHeight"
      :items="filteredPointsOfInterest"
      id="points_dataTable"
      @item-expanded="scrollToRowExpanded"
      item-key="id"
      :footer-props="{
        itemsPerPageText: 'Mis Lugares por página',
        itemsPerPageOptions: itemsPerPageOptions,
        pageText: `${page} de ${totalPages}`,
        disablePagination: loading
      }"
      :items-per-page.sync="itemsPerPage"
      calculate-widths
      :loading="loading"
      :page.sync="page"
      :loading-text="loadingText"
      :no-data-text="dataMessage"
      sort-by="name"
      :custom-sort="customSort"
      ref="pointsOfInterestDataTable"
      show-expand
      single-expand
    >
      <template v-slot:top>
        <div class="card--title">
          <v-toolbar height="51" rounded="lg" flat>
            <v-toolbar-title class="ml-md-6">
              {{
                $t(
                  'fleet_management.points_of_interest.data_table.labels.data_table_toolbar_title'
                )
              }}
              (
              {{ totalPointsOfInterest }}
              )
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              id="idBtnPuntosAdministracionActivarTodas"
              @click="doAllPointsSelected"
              small
              outlined
              color="secondary"
              rounded
              class="mr-2"
              v-if="pointsStorageIsEmpty()"
              :loading="loading"
            >
              {{
                $t(
                  'fleet_management.points_of_interest.data_table.labels.select_all'
                )
              }}
            </v-btn>
            <v-btn
              v-else
              small
              outlined
              @click="removeAllPointsOfInterest"
              color="secondary"
              rounded
              class="mr-2"
              :loading="loading"
            >
              {{
                $t(
                  'fleet_management.points_of_interest.data_table.labels.disable_all'
                )
              }}
            </v-btn>
            <router-link :to="{ name: 'pointsofinterest-create' }">
              <v-btn v-if="isAdmin || isAdminSubFleet" text>
                <v-icon class="tertiary--text"
                  >mdi-map-marker-plus-outline</v-icon
                >
                <span class="text-capitalize text-body-2">
                  {{
                    $t(
                      'fleet_management.points_of_interest.data_table.labels.data_table_toolbar_button'
                    )
                  }}
                </span>
              </v-btn>
            </router-link>
          </v-toolbar>
        </div>
      </template>

      <template v-slot:header.hidePoint="{ header }">
        <span id="idLugaresSeleccion" class="ml-4">{{ header.text }}</span>
      </template>
      <template v-slot:header.name="{ header }">
        <span id="idLugaresNombre" class="ml-4">{{ header.text }}</span>
      </template>
      <template v-slot:header.type="{ header }">
        <span id="idLugaresForma" class="ml-4">{{ header.text }}</span>
      </template>

      <template v-slot:item.hidePoint="{ item, index }">
        <v-row align="center" justify="space-around">
          <v-btn
            :id="`idLugaresAdminitracionActivarLugar${index}`"
            text
            small
            rounded
            @click="showPointsRow(item)"
          >
            <SvgIcon
              :class="[activateClassPoints(item)]"
              iconType="icon_points_of_interest"
            />
          </v-btn>
        </v-row>
      </template>
      <template v-slot:item.name="{ item }">
        <v-tooltip top content-class="secondary white--text">
          <template v-slot:activator="{ on, attrs }">
            <span class="searchable label-links" v-bind="attrs" v-on="on">
              {{ item.label }}
            </span>
          </template>
          <span>
            {{ item.label }}
          </span>
        </v-tooltip>
      </template>
      <template v-slot:item.type="{ item }">
        <v-icon :color="getIconColor(item)">{{ item.icon }}</v-icon>
      </template>

      <template v-slot:item.data-table-expand="{ item, expand, isExpanded, index }">
        <div class="d-flex align-center">
          <v-menu
            offset-x
            nudge-left="12"
            left
            rounded-xl
            content-class="white pa-1"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                :id="`idBtnLugaresKebabMenu${index}`"
                v-if="menuVisibleByAdminOrAdminSubfleet"
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <div>
              <v-btn
                id="idBtnLugaresEditarLugar"
                text
                small
                @click="viewPointsDetail(item)"
              >
                <SvgIcon class="tertiary--text" iconType="icon_edit_pen" />
                <span class="text-capitalize text-body-2">
                  {{
                    $t(
                      'fleet_management.points_of_interest.data_table.labels.button_edit'
                    )
                  }}
                </span>
              </v-btn>
              <v-btn
                id="idBtnLugaresEliminarLugar"
                text
                small
                @click="deletePoint(item)"
              >
                <v-icon small color="primary" class="mr-1">
                  mdi-trash-can-outline
                </v-icon>
                <span class="text-body-2 text-capitalize">{{
                  $t(
                    'fleet_management.points_of_interest.data_table.labels.button_delete'
                  )
                }}</span>
              </v-btn>
            </div>
          </v-menu>

          <v-icon
            id="idPuntosDesplegable"
            v-if="checkDescription(item)"
            class="v-data-table__expand-icon"
            :class="{ 'v-data-table__expand-icon--active': isExpanded }"
            @click="expand(!isExpanded)"
          >
            mdi-chevron-down
          </v-icon>
        </div>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <RowExpanded :item="item" :isAdmin="isAdmin" />
        </td>
      </template>
    </v-data-table>
    <Dialog
      idCancelButton="idBtnLugaresNoLugares"
      idSubmitButton="idBtnLugaresSiLugares"
      idAcceptButton="idBtnLugaresAceptarLugares"
      v-if="showDialog"
      :showDialog="showDialog"
      :oneButton="dialog.oneButton"
      :text="dialog.text"
      :closeButtonText="dialog.closeButtonText"
      :submitButtonText="dialog.acceptButtonText"
      :oneButtonText="dialog.oneButtonText"
      :type="dialog.type"
      :loading="dialog.loading"
      @cancelButtonPressed="cancelDialog"
      @submitButtonPressed="submitPointDelete"
      @oneButtonAction="cancelDialog"
    />
  </v-card>
</template>

<script>
import { i18n } from '@leapsight/portal'
import { SvgIcon } from '@leapsight/styleguide'
import RowExpanded from './RowExpanded'
import { PointsHelpers } from '@/helpers/mainExports.js'
import { mapActions, mapGetters } from 'vuex'
import Dialog from '@/components/dialog/Dialog'
import Mark from 'mark.js'
import store from '@/store'

export default {
  name: 'PointsList',
  components: {
    SvgIcon,
    Dialog,
    RowExpanded
  },
  data() {
    return {
      itemsPerPageOptions: [10, 15],
      page: 1,
      itemsPerPage: 10,
      context: '',
      tableHeight: 50,
      currentUser: localStorage.getItem('user_id'),
      dataMessage: `${i18n.t(
        'fleet_management.points_of_interest.data_table.labels.data_table_no_data'
      )}`,
      dataTableHeight: 0,
      dialog: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButton: false,
        oneButtonText: ''
      },
      dialogDefault: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButton: false,
        oneButtonText: ''
      },
      expanded: [],
      pointToDelete: '',
      headers: [
        {
          text: i18n.t(
            'fleet_management.points_of_interest.data_table.labels.data_table_header_selection'
          ),
          value: 'hidePoint',
          align: 'center',
          class: 'hide-arrow-v-icon'
        },
        {
          text: i18n.t(
            'fleet_management.points_of_interest.data_table.labels.data_table_header_name'
          ),
          value: 'name',
          align: 'center',
          class: 'secondary--text'
        },
        {
          text: i18n.t(
            'fleet_management.points_of_interest.data_table.labels.data_table_header_type'
          ),
          value: 'type',
          align: 'center',
          class: 'secondary--text'
        },
        {
          text: '',
          value: 'data-table-expand',
          align: 'start'
        }
      ],
      inputTerm: '',
      loading: false,
      loadingText: i18n.t(
        'fleet_management.points_of_interest.data_table.labels.loading_text'
      ),
      markInstance: {},
      matchPosition: 1,
      searchMatch: [],
      showDialog: false,
      pointsSelected: [],
      sessionHandled: false
    }
  },
  methods: {
    ...mapActions(['setSearchMatch']),
    ...mapActions('poi', ['getPointsList']),
    checkDescription(item) {
      return !!item.description && item.description.trim() !== ''
    },
    getIconColor(item) {
      return item.icon === 'mdi-star-outline' ? 'black' : item.color
    },
    doAllPointsSelected() {
      try {
        this.loading = true

        this.$store.dispatch('doAllPointsSelected', value => {
          this.pointsSelected = value
          return this.pointsSelected
        })
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async loadPointSelected() {
      try {
        this.loading = true
        const updateFilterPoint = this.points.map(p => {
          if (this.getPointsStorage().includes(p.id)) p.hidePoint = false

          return p
        })
        this.pointsSelected = updateFilterPoint.filter(point =>
          this.getPointsStorage().includes(point.id)
        )

        this.$store.commit('setFilterPointsByString', {
          filterPointsOfInterest: updateFilterPoint
        })

        if (this.showPointsOfInterestOnMap) {
          const promisesPointsSelected = []
          const pointsStorage = this.getPointsStorage()
          pointsStorage.forEach(id => {
            promisesPointsSelected.push(PointsHelpers.getPoint({ id }))
          })
          const results = await Promise.allSettled(promisesPointsSelected)
          this.pointsSelected = results.map(result => {
            const { value } = result
            Object.assign(value, { id: value.id })
            if (Boolean(value)) {
              return value
            } else {
              return {}
            }
          })

          this.$store.dispatch('destroyPointsOnMap')
          PointsHelpers.pointOnMap(this.pointsSelected, this.map)
        }
      } catch (e) {
        console.log(e)
      } finally {
        this.loading = false
      }
    },
    getPointsStorage() {
      return JSON.parse(localStorage.getItem('pointsSelected') ?? '[]')
    },
    removeAllPointsOfInterest() {
      this.pointsSelected = []
      this.points.forEach(point => {
        point.hidePoint = true
      })
      localStorage.setItem('pointsSelected', '[]')
      this.$store.dispatch('destroyPointsOnMap')
      PointsHelpers.pointOnMap(this.pointsSelected, this.map)
    },
    activateClassPoints(item) {
      const activateClass = Boolean(this.getPointsStorage().includes(item.id))
      if (activateClass && !this.showPointsOfInterestOnMap) return 'black--text'
      if (activateClass && this.showPointsOfInterestOnMap)
        return 'primary--text'
      return 'grey--text'
    },
    async fillPoints() {
      try {
        const response = await PointsHelpers.getPoints()
        if (!response.hasOwnProperty('error')) {
          const points = response.map(item => {
            item.id = item.id
            item.hidePoint = true
            item.created_by = item.created_by
            return item
          })

          this.$store.commit('setPoints', { points })
          this.$store.commit('resetFilterPointsState')

          this.loading = false
        } else {
          this.loading = false
          this.dataMessage = `${i18n.t(
            'fleet_management.points_of_interest.data_table.labels.data_table_error'
          )}`
        }
      } catch (error) {
        const errorText = PointsHelpers.getErrorMessageByCode(error.error)
        const snackbar = {
          text: errorText
        }
        store.dispatch('ui/setSnackbar', snackbar)
        this.loading = false
      }
    },
    async fillPointDataById(id) {
      try {
        this.loading = true
        const res = await PointsHelpers.getPoint({ id })
        const idx = this.getPointsSortedByName.findIndex(p => p.id === id)
        Object.assign(this.getPointsSortedByName[idx], res)
      } catch (error) {
        const errorText = PointsHelpers.getErrorMessageByCode(error.error)
        const snackbar = {
          text: errorText
        }
        this.$store.dispatch('ui/setSnackbar', snackbar)
      } finally {
        this.loading = false
      }
    },
    async showPointsRow(value) {
      const id = value.id

      await this.fillPointDataById(id)

      if (!this.showPointsOfInterestOnMap) await this.loadPointSelected()

      if (!this.pointsSelected.some(p => p.id == id)) {
        value.hidePoint = false
        this.pointsSelected.push(value)
      } else {
        value.hidePoint = true
        this.pointsSelected = this.pointsSelected.filter(
          points => points.id != id
        )
      }

      const pointsId = this.pointsSelected.map(point => point.id)
      localStorage.setItem('pointsSelected', JSON.stringify(pointsId))
      this.$store.commit('setPointsInLocalStorage')

      this.$store.dispatch('destroyPointsOnMap')
      PointsHelpers.pointOnMap(this.pointsSelected, this.map)
    },
    cancelDialog() {
      this.showDialog = false
      Object.assign(this.dialog, this.dialogDefault)
    },
    deletePoint(item) {
      const { label: pointName } = item

      this.showDialog = true
      this.dialog.text = i18n.t(
        'fleet_management.points_of_interest.delete_points_of_interest.labels.dialog_text',
        { pointName }
      )
      this.dialog.type = 'warning'
      this.dialog.closeButtonText = i18n.t(
        'fleet_management.points_of_interest.delete_points_of_interest.labels.dialog_closeButton'
      )
      this.dialog.acceptButtonText = i18n.t(
        'fleet_management.points_of_interest.delete_points_of_interest.labels.dialog_acceptButton'
      )

      this.pointToDelete = item
      this.$store.dispatch('destroyPointsOnMap')
    },
    async submitPointDelete() {
      const id = this.pointToDelete.id
      try {
        this.dialog.loading = true
        await this.$store.dispatch('deletePoint', id)
        await this.fillPoints()

        const pointsStorage = this.getPointsStorage()
        const updatedPointsStorage = pointsStorage.filter(p => p !== id)
        localStorage.setItem(
          'pointsSelected',
          JSON.stringify(updatedPointsStorage)
        )

        this.dialog.loading = false
        this.dialog.text = i18n.t('general.dialogComponent.title_success')
        this.dialog.oneButton = true
        this.dialog.oneButtonText = i18n.t(
          'general.dialogComponent.acceptButtonText'
        )
        this.dialog.type = 'success'
      } catch (error) {
        const { error: codeError } = error
        const errorText = PointsHelpers.getErrorMessageByCode(codeError)
        this.dialog.loading = false
        this.dialog.type = 'error'
        this.dialog.text = errorText
        this.dialog.oneButton = true
        this.dialog.oneButtonText = i18n.t(
          'general.dialogComponent.acceptButtonText'
        )
      }
    },
    viewPointsDetail(item) {
      this.$router.push({ name: 'PointDetail', params: { id: item.id } })
    },
    resize() {
      //Columna datatable
      const dataTableColumn = document.getElementById('points_dataTable')
        .parentElement.parentElement
      // Alto del div contenedor
      const dataTableColumnHeight = dataTableColumn.offsetHeight

      // Padding total de la columna de la tabla
      const compStylesDataTableColumn = window.getComputedStyle(dataTableColumn)
      const paddingDataTableCol =
        parseInt(compStylesDataTableColumn.getPropertyValue('padding-top')) +
        parseInt(compStylesDataTableColumn.getPropertyValue('padding-bottom'))

      //Alto y margen bottom del componente titulo y buscador
      const marginBottomTitleAndSearch = parseInt(
        window
          .getComputedStyle(document.getElementById('titleAndSearch'))
          .getPropertyValue('margin-bottom')
      )
      const titleAndSearchHeight =
        document.querySelector('#titleAndSearch').offsetHeight +
        marginBottomTitleAndSearch

      // Alto Card Title
      const cardTitleHeight = document.querySelector(
        '#points_dataTable .card--title'
      ).offsetHeight

      this.dataTableHeight =
        dataTableColumnHeight -
        paddingDataTableCol -
        titleAndSearchHeight -
        cardTitleHeight
    },
    scrollToRowExpanded(data) {
      if (data.value) {
        //A vuetifiy le lleva un tiempo añadir las clases de row y expanded
        setTimeout(() => {
          const selectedRow = document.querySelector(
            '.v-data-table__expanded__content'
          ).previousElementSibling
          const tableHeader = document.querySelector('.v-data-table-header')
            .clientHeight
          const barToScroll = document.querySelector(
            '#points_dataTable .v-data-table__wrapper'
          )
          selectedRow.scrollIntoView()
          //Despues de llevar el row al top de la vista, scrollea la altura del header
          barToScroll.scrollBy({
            top: -tableHeader,
            behaviour: 'smooth'
          })
        }, 10)
      }
    },
    searchAndHighlight(inputTerm) {
      const keyword = inputTerm
      const marked = this.markInstance?.unmark ? this.markInstance : new Mark()
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const actualThis = this
      this.searchMatch = []

      marked.unmark({
        done: function() {
          marked.mark(keyword, {
            separateWordSearch: false,
            noMatch: function() {
              // Esta funcion se ejecuta cuando no hay match
            },
            each: function(node) {
              // Array con los row que machean a la busqueda
              actualThis.searchMatch.push(node.closest('tr'))
            }
          })
        }
      })

      this.setSearchMatch(this.searchMatch)
      //Damos estilo al match activo si el listado de nodos encontrados es mayor a 0
      const activePosition = document.querySelectorAll('mark')

      if (activePosition.length > 0)
        activePosition[this.matchPosition - 1].classList.add('activeMatch')

      // Scrollintoview para el match activo
      if (this.searchMatch.length > 0) {
        const selectedRow = this.searchMatch[this.matchPosition - 1]
        const dataTablelastRow = document.querySelector(
          '#points_dataTable .v-data-table__wrapper table tbody tr:last-child'
        )
        const tableHeader = document.querySelector('.v-data-table-header')
          .clientHeight
        const barToScroll = document.querySelector(
          '#points_dataTable .v-data-table__wrapper'
        )

        selectedRow.scrollIntoView()

        // Esta validacion es para hacer referencia al ultimo row ya que por defecto no se visualiza
        if (selectedRow === dataTablelastRow) {
          barToScroll.scrollBy({
            top: 0,
            behaviour: 'smooth'
          })
        } else {
          barToScroll.scrollBy({
            top: -tableHeader,
            behaviour: 'smooth'
          })
        }
      }
    },
    pointsStorageIsEmpty() {
      return !Boolean(this.getPointsStorage().length)
    },
    customSort(items, index, isDesc) {
      const fieldMap = {
        name: 'label'
      }

      const field = fieldMap[index[0]]

      if (!field || typeof isDesc[0] !== 'boolean') {
        return items
      }
      const sortedItems = [...items]

      sortedItems.sort((a, b) => {
        const firstValue = (a[field]?.toString().toLowerCase() || '').trim()
        const secondValue = (b[field]?.toString().toLowerCase() || '').trim()

        if (isDesc[0]) {
          return secondValue.localeCompare(firstValue)
        } else {
          return firstValue.localeCompare(secondValue)
        }
      })

      return sortedItems
    }
  },
  computed: {
    ...mapGetters('auth', ['connection', 'session']),
    ...mapGetters('mapState', ['map']),
    ...mapGetters([
      'getSearchInput',
      'getMatchPosition',
      'isAdmin',
      'isAdminSubFleet',
      'showPointsOfInterestOnMap',
      'getPointsSortedByName'
    ]),
    menuVisibleByAdminOrAdminSubfleet() {
      return this.isAdmin || this.isAdminSubFleet
    },
    filteredPointsOfInterest() {
      return this.$store.getters.filteredPointsOfInterest
    },
    points() {
      return this.$store.state.pointsOfInterest
    },
    totalPages() {
      return Math.ceil(this.filteredPointsOfInterest.length / this.itemsPerPage)
    },
    height() {
      return this.dataTableHeight
    },
    totalPointsOfInterest() {
      return this.filteredPointsOfInterest.length
    }
  },
  async created() {
    this.loading = true
    if (this.session.isOpen && !this.sessionHandled) {
      this.sessionHandled = true
      await this.fillPoints()
      await this.loadPointSelected()
      this.loading = false
    }
  },
  watch: {
    async session(newSession) {
      if (newSession.isOpen && !this.sessionHandled) {
        this.sessionHandled = true
        this.loading = true
        await this.fillPoints()
        this.loading = false
      }
    },
    getSearchInput() {
      const keyword = this.getSearchInput
      this.searchAndHighlight(keyword)
    },
    getMatchPosition() {
      this.matchPosition = this.getMatchPosition
      const keyword = this.getSearchInput
      this.searchAndHighlight(keyword)
    },
    showPointsOfInterestOnMap(value) {
      if (!value) {
        this.pointsSelected = []
      } else {
        this.loadPointSelected()
        this.$store.dispatch('destroyPointsOnMap')
        PointsHelpers.pointOnMap(this.pointsSelected, this.map)
      }
    }
  },
  updated() {
    this.$nextTick(function() {
      this.context = document.querySelectorAll('.searchable')
      this.markInstance = new Mark(this.context)
    })
  },
  mounted() {
    addEventListener('resize', this.resize)
    this.$nextTick(function() {
      this.resize()
    })
  },
  beforeDestroy() {
    removeEventListener('resize', this.resize)
  }
}
</script>
<style lang="scss" scoped>
.scrollable-card {
  max-height: 200px;
  overflow-y: auto;
}
.v-menu__content {
  contain: initial;
  overflow: visible;
}
.v-menu__content::before {
  position: absolute;
  content: '';
  top: 50%;
  right: -12px;
  transform: translateY(-50%);
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
}
.v-btn.btn__activator-not-focus:not(.v-btn--text):not(.v-btn--outlined):focus:before {
  opacity: 0;
}
span.label-links:hover {
  text-decoration-line: underline;
  color: var(--deep-sky-blue) !important;
}
</style>
<style lang="scss">
#points_dataTable {
  .v-toolbar__title {
    font-weight: bold;
    font-size: 0.9375rem;
  }

  mark {
    background-color: var(--yellow-lighten-1);
    &.activeMatch {
      background-color: var(--orange-ligthen-1);
    }
  }
}
</style>
